/**
 * Аудитория — рабочий стол, а не один большой график.
 *
 * Главный показатель ровно один: сколько людей в канале сейчас. Всё
 * остальное — контекст к нему, и поэтому оно мельче. Три одинаковые плитки
 * рядом заставляли бы каждый раз решать, какая из них главная.
 *
 * Сравнение с прошлым периодом берётся из того же ответа API: он умеет отдать
 * вдвое более длинный ряд, и прошлый период — это просто его первая половина,
 * сдвинутая по датам. Никаких придуманных чисел для «красивого пунктира».
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useSession } from "../session";
import {
  Card,
  Delta,
  Empty,
  ErrorBox,
  FilterBar,
  FilterGroup,
  LoadingDashboard,
  MetricCard,
  PageHeader,
  Paywall,
  SegmentedControl,
} from "../components/ui";
import { ChartCard, TimeSeriesChart, type ChartSeries } from "../components/charts";
import { dayShort, num, parseDay, plural, signed } from "../lib/format";

interface AudiencePoint {
  date: string;
  subscribers: number;
  joined: number | null;
  left: number | null;
}

interface AudienceResponse {
  series: AudiencePoint[];
  history_days: number;
  median_daily_delta: number | null;
  has_join_leave: boolean;
  limitations: string[];
}

const RANGES = [30, 90, 180] as const;
type Range = (typeof RANGES)[number];
type Metric = "level" | "delta" | "flow";

const MAX_DAYS = 365;

export default function AudiencePage() {
  const { channelId } = useSession();
  const [range, setRange] = useState<Range>(90);
  const [compare, setCompare] = useState(true);
  const [metric, setMetric] = useState<Metric>("level");

  // Прошлый период — это просто более длинный запрос: сервер отдаёт ряд от
  // сегодня назад, и вторая его половина и есть «как было».
  const days = Math.min(compare ? range * 2 : range, MAX_DAYS);

  const query = useQuery({
    queryKey: ["audience", channelId, days],
    queryFn: () => api.get<AudienceResponse>(`/analytics/${channelId}/audience?days=${days}`),
    enabled: Boolean(channelId),
  });

  const data = query.data;
  const view = useMemo(() => (data ? split(data.series, range, compare) : null), [data, range, compare]);

  if (!channelId) {
    return (
      <Card>
        <Empty
          title="Сначала подключите канал"
          actions={
            <Link to="/channels">
              <button className="primary">Подключить канал</button>
            </Link>
          }
        />
      </Card>
    );
  }
  if (query.error instanceof ApiError && query.error.isPaywall) return <Paywall what="Разбор аудитории" />;
  if (query.isLoading || !data || !view) return <LoadingDashboard />;
  if (query.error) return <ErrorBox error={query.error} />;

  const { current, previousByCurrentDate } = view;
  const latest = current.at(-1);
  const first = current[0];
  const change = latest && first ? latest.subscribers - first.subscribers : null;
  const changePct = change !== null && first && first.subscribers ? (change / first.subscribers) * 100 : null;

  const previousChange = (() => {
    const values = current.map((point) => previousByCurrentDate.get(point.date)).filter((v): v is AudiencePoint => Boolean(v));
    if (values.length < 2) return null;
    return values[values.length - 1].subscribers - values[0].subscribers;
  })();

  const dates = current.map((point) => point.date);
  const series = buildSeries(metric, current, previousByCurrentDate, compare, range);

  const hasFlow = data.has_join_leave;

  return (
    <>
      <PageHeader
        eyebrow="Детальная аналитика"
        title="Аудитория"
        lede="Ряд начинается со дня подключения: задним числом число подписчиков не восстановить ни на одной площадке."
      />

      <FilterBar>
        <FilterGroup label="Период">
          <SegmentedControl
            value={String(range)}
            onChange={(value) => setRange(Number(value) as Range)}
            ariaLabel="Период"
            options={RANGES.map((value) => ({ value: String(value), label: `${value} дней` }))}
          />
        </FilterGroup>
        <FilterGroup label="Сравнить">
          <SegmentedControl
            value={compare ? "on" : "off"}
            onChange={(value) => setCompare(value === "on")}
            ariaLabel="Сравнение с прошлым периодом"
            options={[
              { value: "off", label: "Без сравнения" },
              {
                value: "on",
                label: "С прошлым периодом",
                title: `Тот же отрезок, сдвинутый на ${range} дней назад`,
              },
            ]}
          />
        </FilterGroup>
        <span className="spacer" />
        <span className="faint">
          история наблюдений: {num(data.history_days)} {plural(data.history_days, "сутки", "суток", "суток")}
        </span>
      </FilterBar>

      {/* Один главный показатель — и контекст к нему, а не четыре равных плитки. */}
      <div className="grid" style={{ gridTemplateColumns: "minmax(280px, 1.4fr) repeat(auto-fit, minmax(190px, 1fr))" }}>
        <MetricCard
          hero
          name="Подписчиков сейчас"
          value={num(latest?.subscribers ?? null)}
          delta={
            <Delta
              value={change}
              formatted={
                change === null
                  ? undefined
                  : `${signed(change)}${changePct === null ? "" : ` · ${signed(changePct, 1)}%`}`
              }
              note={`за ${range} дней`}
            />
          }
          context={
            latest
              ? `Последний замер — ${dayShort(latest.date)}. ${
                  previousChange === null
                    ? "Прошлый период сравнить не с чем: замеров за него нет."
                    : `Прошлый период дал ${signed(previousChange)}.`
                }`
              : "За период нет ни одного замера."
          }
          spark={current.map((point) => point.subscribers)}
        />
        <MetricCard
          name="Обычный прирост в сутки"
          value={data.median_daily_delta === null ? "—" : signed(data.median_daily_delta, 1)}
          context="Медиана, а не среднее: один день закупа не должен переписывать норму."
        />
        <MetricCard
          name="Замеров за период"
          value={num(current.length)}
          context={
            current.length < range
              ? `Сутки без замера — это пропуск, а не ноль: в ряду они останутся разрывом.`
              : "Замер каждые сутки — ряд полный."
          }
        />
      </div>

      <ChartCard
        title={TITLES[metric]}
        sub={SUBS[metric]}
        controls={
          <SegmentedControl
            value={metric}
            onChange={setMetric}
            ariaLabel="Показатель"
            options={[
              { value: "level", label: "Сколько всего" },
              { value: "delta", label: "Прирост за сутки" },
              ...(hasFlow ? [{ value: "flow" as Metric, label: "Пришло и ушло" }] : []),
            ]}
          />
        }
        series={series}
        height={320}
        footnote={NOTES[metric]}
      >
        {current.length < 2 ? (
          <div className="chart-empty">
            <span>
              Замеров пока {current.length}. График появится, когда их станет хотя бы два — по одному в
              сутки.
            </span>
          </div>
        ) : (
          <TimeSeriesChart
            dates={dates}
            series={series}
            height={320}
            formatTip={(value) => (metric === "level" ? num(Math.round(value)) : signed(value))}
            includeZero={metric !== "level"}
            tipFooter={(index) => {
              // В подвале — то, чего НЕТ в строках выше: разница с тем же днём
              // прошлого периода. Повторять «+8», которое уже стоит рядом со
              // значением, значит занимать место ничем.
              const point = current[index];
              if (!point) return null;
              const was = previousByCurrentDate.get(point.date);
              if (metric === "level" && was) {
                const gap = point.subscribers - was.subscribers;
                return `${signed(gap)} к тому же дню прошлого периода`;
              }
              return null;
            }}
          />
        )}
      </ChartCard>

      <Card title="Что здесь нельзя измерить" note="границы данных — часть показателя">
        <ul className="muted" style={{ paddingLeft: 18, margin: 0, display: "grid", gap: 8 }}>
          {data.limitations.map((line) => (
            <li key={line}>{line}</li>
          ))}
          {!hasFlow && (
            <li>
              Приход и уход по отдельности площадка показывает только администратору канала. Без прав
              виден лишь чистый прирост — а значит, приход от закупок занижен ровно на отток.
            </li>
          )}
          {compare && days === MAX_DAYS && range * 2 > MAX_DAYS && (
            <li>
              Сравнение обрезано: API отдаёт не больше {MAX_DAYS} суток, и прошлый период попал в них не
              целиком.
            </li>
          )}
        </ul>
      </Card>
    </>
  );
}

const TITLES: Record<Metric, string> = {
  level: "Как росла аудитория",
  delta: "Прирост по суткам",
  flow: "Кто пришёл и кто ушёл",
};
const SUBS: Record<Metric, string> = {
  level: "Сколько человек было в канале на конец каждых суток",
  delta: "Разница между соседними замерами — то, из чего складывается общий итог",
  flow: "Приход и отток по отдельности — видны только под правами администратора",
};
const NOTES: Record<Metric, string> = {
  level:
    "Разрыв в линии — сутки без замера. Соединять его значит нарисовать измерение, которого не было.",
  delta:
    "Прирост считается между соседними ЗАМЕРАМИ, а не сутками: если замера не было, разница отнесена к тому дню, когда он появился.",
  flow: "Уход показан вниз от нуля: так видно, что чистый прирост — это разность двух потоков, а не одно число.",
};

/** Ряд от сервера режется по датам, а не по номерам точек: пропущенные сутки
 *  сместили бы «прошлый период» на несколько дней и тихо сломали сравнение. */
function split(series: AudiencePoint[], range: number, compare: boolean) {
  if (!series.length) return { current: [], previousByCurrentDate: new Map<string, AudiencePoint>() };
  const last = parseDay(series[series.length - 1].date);
  const cutoff = new Date(last);
  cutoff.setDate(cutoff.getDate() - range);

  const current = series.filter((point) => parseDay(point.date) > cutoff);
  const byDate = new Map(series.map((point) => [point.date, point]));

  const previousByCurrentDate = new Map<string, AudiencePoint>();
  if (compare) {
    for (const point of current) {
      const shifted = parseDay(point.date);
      shifted.setDate(shifted.getDate() - range);
      const key = `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, "0")}-${String(
        shifted.getDate(),
      ).padStart(2, "0")}`;
      const match = byDate.get(key);
      if (match) previousByCurrentDate.set(point.date, match);
    }
  }
  return { current, previousByCurrentDate };
}

function buildSeries(
  metric: Metric,
  current: AudiencePoint[],
  previousByCurrentDate: Map<string, AudiencePoint>,
  compare: boolean,
  range: number,
): ChartSeries[] {
  const compareDates = current.map((point) => previousByCurrentDate.get(point.date)?.date ?? point.date);
  const hasCompare = compare && previousByCurrentDate.size > 1;

  if (metric === "flow") {
    return [
      {
        key: "joined",
        label: "Пришло",
        color: "var(--positive)",
        kind: "bar",
        values: current.map((point) => point.joined),
      },
      {
        key: "left",
        label: "Ушло",
        color: "var(--negative)",
        kind: "bar",
        values: current.map((point) => (point.left === null ? null : -Math.abs(point.left))),
      },
    ];
  }

  if (metric === "delta") {
    const deltas = current.map((point, index) =>
      index === 0 ? null : point.subscribers - current[index - 1].subscribers,
    );
    const out: ChartSeries[] = [
      {
        key: "delta",
        label: "Прирост за сутки",
        color: "var(--chart-1)",
        kind: "bar",
        values: deltas,
      },
    ];
    if (hasCompare) {
      out.push({
        key: "delta-prev",
        label: `Прошлый период (−${range} дней)`,
        color: "var(--chart-2)",
        kind: "bar",
        compare: true,
        dates: compareDates,
        values: current.map((point, index) => {
          const now = previousByCurrentDate.get(point.date);
          const before = index === 0 ? undefined : previousByCurrentDate.get(current[index - 1].date);
          return now && before ? now.subscribers - before.subscribers : null;
        }),
      });
    }
    return out;
  }

  const out: ChartSeries[] = [
    {
      key: "subs",
      label: "Подписчики",
      color: "var(--chart-1)",
      kind: "area",
      values: current.map((point) => point.subscribers),
      format: (value) => `${num(Math.round(value))} чел.`,
    },
  ];
  if (hasCompare) {
    out.push({
      key: "subs-prev",
      label: `Прошлый период (−${range} дней)`,
      color: "var(--chart-2)",
      compare: true,
      dates: compareDates,
      values: current.map((point) => previousByCurrentDate.get(point.date)?.subscribers ?? null),
      format: (value) => `${num(Math.round(value))} чел.`,
    });
  }
  return out;
}
